import React, { useState, useEffect, useCallback } from "react";
import { get, post, patch } from "../../../helpers/api_helper";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import {
  Card, CardBody, Row, Col, Button, Spinner,
  Modal, ModalHeader, ModalBody, Label, Input, Alert,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import PageHint from '../../../components/Common/PageHint';
import "react-dropzone-uploader/dist/styles.css";
import Dropzone from "react-dropzone-uploader";
import { makeUploadParams } from "../../../helpers/imageResize";

const ODATA_BASE = "https://buharaapi.stechomeyazilim.info";
const IMG_BASE = "https://buharasubdomain.stechomeyazilim.info/";
const UPLOAD_URL = "https://buharaadmin.stechomeyazilim.info:8099/uploadfile";

// Slot anahtarlarının panelde okunur karşılıkları ve hangi sayfada
// göründüğü. Veritabanındaki Site_Image_Title boşsa buradaki metin kullanılır.
const SLOT_LABELS = {
  about_hero:     { group: "Hakkımızda",  label: "Üst banner",                    hint: "Sayfanın en üstündeki geniş görsel (öneri: 1920×600)" },
  about_1:        { group: "Hakkımızda",  label: "Büyük görsel",                  hint: "Metnin sağındaki büyük kare görsel (öneri: 800×1000)" },
  about_2:        { group: "Hakkımızda",  label: "Küçük görsel 1",                hint: "Sağ üstteki küçük görsel (öneri: 800×500)" },
  about_3:        { group: "Hakkımızda",  label: "Küçük görsel 2",                hint: "Sağ alttaki küçük görsel (öneri: 800×500)" },
  home_recipes_1: { group: "Anasayfa",    label: "Tariflere Göz At — sol kutu",   hint: "\"Trend Tarifler\" kutusu (öneri: 1000×600)" },
  home_recipes_2: { group: "Anasayfa",    label: "Tariflere Göz At — sağ kutu",   hint: "\"En Gözde Tarifler\" kutusu (öneri: 1000×600)" },
  dealers_hero:   { group: "Bayilerimiz", label: "Üst banner",                    hint: "Bayiler sayfasının üst görseli (öneri: 1920×600)" },
  recipes_hero:   { group: "Tarifler",    label: "Üst banner",                    hint: "Tarif listesi sayfasının üst görseli (öneri: 1920×600)" },
  blog_hero:      { group: "Blog",        label: "Üst banner",                    hint: "Blog sayfasının üst görseli (öneri: 1920×600)" },

  // Yeni sitede kullanılan ek yuvalar
  production_hero:   { group: "Üretim",           label: "Üst banner",     hint: "Üretim sayfasının üst görseli (öneri: 1920×600)" },
  products_hero:     { group: "Ürünler",          label: "Üst banner",     hint: "Ürünler sayfasının üst görseli (öneri: 1920×600)" },
  certificates_hero: { group: "Kalite Belgeleri", label: "Üst banner",     hint: "Kalite belgeleri sayfasının üst görseli (öneri: 1920×600)" },
  contact_hero:      { group: "İletişim",         label: "Üst banner",     hint: "İletişim sayfasının üst görseli (öneri: 1920×600)" },
  catalogs_hero:     { group: "E-Katalog",        label: "Üst banner",     hint: "E-Katalog sayfasının üst görseli (öneri: 1920×600)" },

  // Görsel değil video yuvası: dosya yüklenebilir ya da YouTube/Vimeo bağlantısı girilebilir.
  about_video:       { group: "Hakkımızda", label: "Tanıtım filmi", video: true,
                       hint: "Hakkımızda sayfasında metnin altında oynatılır. YouTube/Vimeo bağlantısı önerilir; dosya yüklenecekse mp4 ve 50 MB altı olmalı." },
};

const isLink = (v) => /^https?:\/\//i.test(v || "");
const videoEmbed = (v) => {
  const yt = (v || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vimeo = (v || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};
const mediaSrc = (v) => (isLink(v) ? v : IMG_BASE + v);

/** Yuvadaki görseli ya da videoyu önizler. */
function SlotPreview({ value, video, style }) {
  if (!video) return <img src={IMG_BASE + value} alt="" style={style} />;
  const embed = videoEmbed(value);
  return embed
    ? <iframe src={embed} title="video" style={{ ...style, border: 0 }} allowFullScreen />
    : <video src={mediaSrc(value)} controls preload="metadata" style={{ ...style, background: "#000" }} />;
}

const GROUP_ORDER = [
  "Anasayfa", "Hakkımızda", "Üretim", "Ürünler", "Kalite Belgeleri",
  "Bayilerimiz", "Tarifler", "Blog", "E-Katalog", "İletişim", "Diğer",
];

export default function SiteImagesPage() {
  document.title = "Site Görselleri | BUHARA - Admin Paneli";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [current, setCurrent] = useState(null);   // düzenlenen slot
  const [fileName, setFileName] = useState(null); // yeni yüklenen dosya adı
  const [title, setTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");   // video yuvası için bağlantı
  const [notReady, setNotReady] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get(`${ODATA_BASE}/Site_Image?$orderby=Site_Image_Sort`);
      let list = res?.value || [];

      // Sitede kullanılan ama veritabanında henüz satırı olmayan yuvalar
      // (ör. sonradan eklenen "Hakkımızda — tanıtım filmi") ilk açılışta
      // oluşturulur; ayrıca SQL çalıştırmak gerekmez.
      const have = new Set(list.map(r => r.Site_Image_Key));
      const missing = Object.keys(SLOT_LABELS).filter(k => !have.has(k));
      if (missing.length) {
        const maxSort = list.reduce((m, r) => Math.max(m, r.Site_Image_Sort || 0), 0);
        await Promise.all(missing.map((k, i) => post(`${ODATA_BASE}/Site_Image`, {
          Site_Image_Key: k,
          Site_Image_Title: `${SLOT_LABELS[k].group} — ${SLOT_LABELS[k].label}`,
          Site_Image_Sort: maxSort + i + 1,
          Site_Image_IsActive: true,
        }).catch(err => console.error("Site_Image slot create error:", k, err))));
        const again = await get(`${ODATA_BASE}/Site_Image?$orderby=Site_Image_Sort`);
        list = again?.value || list;
      }

      setRows(list);
      setNotReady(false);
    } catch (err) {
      console.error("Site_Image fetch error:", err);
      // Tablo/endpoint henüz yayına alınmadıysa kullanıcıya net bilgi ver
      setNotReady(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // --- Dosya yükleme (mevcut panel akışıyla birebir aynı) ---
  // Görseller sunucuya gitmeden önce tarayıcıda küçültülür (helpers/imageResize).
  const getUploadParams = makeUploadParams(UPLOAD_URL);

  const handleChangeStatus = ({ xhr }) => {
    if (!xhr) return;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        try {
          const result = JSON.parse(xhr.response);
          setFileName(result.file.filename);
          setVideoUrl("");
          toastr.success("Dosya yüklendi. Kaydet'e basmayı unutmayın.");
        } catch (e) {
          console.error(e);
          toastr.error("Görsel yüklenemedi.");
        }
      }
    };
  };

  const openEdit = (row) => {
    setCurrent(row);
    setFileName(null);
    setVideoUrl(isLink(row.Site_Image_Url) ? row.Site_Image_Url : "");
    setTitle(row.Site_Image_Title || "");
    setModal(true);
  };

  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const payload = { Site_Image_Title: title || null };
      // Yeni görsel seçilmediyse mevcut görsel korunur
      if (fileName) payload.Site_Image_Url = fileName;
      else if (meta(current).video && videoUrl.trim() && videoUrl.trim() !== current.Site_Image_Url) {
        if (!isLink(videoUrl.trim())) {
          toastr.error("Bağlantı https:// ile başlamalı.");
          setSaving(false);
          return;
        }
        payload.Site_Image_Url = videoUrl.trim();
      }

      await patch(`${ODATA_BASE}/Site_Image(${current.ID})`, payload);
      toastr.success(meta(current).video ? "Video güncellendi." : "Görsel güncellendi.");
      setModal(false);
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("Kayıt sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (row) => {
    const isVideo = meta(row).video;
    if (!window.confirm(isVideo ? "Videoyu kaldırmak istiyor musunuz? Sayfada video bölümü gizlenir." : "Bu görseli kaldırıp varsayılana dönmek istiyor musunuz?")) return;
    try {
      await patch(`${ODATA_BASE}/Site_Image(${row.ID})`, { Site_Image_Url: null });
      toastr.success(isVideo ? "Video kaldırıldı." : "Görsel kaldırıldı, varsayılana dönüldü.");
      await fetchRows();
    } catch (err) {
      console.error(err);
      toastr.error("İşlem sırasında hata oluştu.");
    }
  };

  const meta = (row) => SLOT_LABELS[row.Site_Image_Key] || {
    group: "Diğer", label: row.Site_Image_Key, hint: "",
  };

  // Slot'ları sayfaya göre grupla
  const grouped = rows.reduce((acc, row) => {
    const g = meta(row).group;
    (acc[g] = acc[g] || []).push(row);
    return acc;
  }, {});
  const groups = GROUP_ORDER.filter(g => grouped[g]?.length);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Yönetim" breadcrumbItem="Site Görselleri" />
          <PageHint
            what="Sayfaların üst bannerları ve sabit görselleri bu ekrandan yönetilir."
            where="Her yuva sitede belirli bir yere denk gelir (Hakkımızda üst banner, Üretim üst banner, Tarifler üst banner...). Bir yuvaya görsel yüklendiğinde o sayfa geniş görselli açılır. Hakkımızda, Üretim ve Kalite Belgeleri üst görselleri mobil uygulamada da kullanılır."
            note="Yuva boş bırakılırsa sayfa sade başlıkla açılır, hata oluşmaz."
            links={[{ label: 'Hakkımızda', path: '/hakkimizda' }, { label: 'Üretim', path: '/uretim' }, { label: 'Tarifler', path: '/yemek-tarifleri' }, { label: 'Blog', path: '/blog' }]}
          />

        {notReady && (
          <Alert color="warning">
            <strong>Site görselleri servisi henüz aktif değil.</strong><br />
            Backend'de <code>Site_Image</code> tablosu oluşturulup API yeniden
            yayınlandıktan sonra bu ekran çalışacaktır. Site bu süre boyunca
            mevcut varsayılan görselleri göstermeye devam eder.
          </Alert>
        )}

        <Card>
          <CardBody>
            <h5 className="mb-1">Sayfa Görselleri</h5>
            <p className="text-muted mb-4">
              Buradaki her kutu, sitedeki sabit bir görsel alanına karşılık gelir.
              Görseli değiştirmek için ilgili kutunun <em>Değiştir</em> butonuna basın.
              Bir alan boş bırakılırsa sitede tasarımdaki varsayılan görsel gösterilir.
            </p>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="warning" /> <span className="ms-2">Yükleniyor...</span>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">Kayıt bulunamadı.</div>
            ) : (
              groups.map(g => (
                <div key={g} className="mb-4">
                  <h6 className="text-uppercase text-muted mb-3">{g}</h6>
                  <Row>
                    {grouped[g].map(row => {
                      const m = meta(row);
                      const hasImage = !!row.Site_Image_Url;
                      return (
                        <Col key={row.ID} md={6} xl={4} className="mb-3">
                          <Card className="border h-100 mb-0">
                            <div
                              style={{
                                height: 160,
                                background: "#f5f6f8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                              }}
                            >
                              {hasImage ? (
                                <SlotPreview
                                  value={row.Site_Image_Url}
                                  video={m.video}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <span className="text-muted">
                                  <i className={`bx ${m.video ? "bx-video" : "bx-image"} font-size-24 d-block text-center`} />
                                  {m.video ? "Video eklenmedi" : "Varsayılan görsel"}
                                </span>
                              )}
                            </div>
                            <CardBody>
                              <h6 className="mb-1">{m.label}</h6>
                              <p className="text-muted font-size-12 mb-3">{m.hint}</p>
                              <Button color="success" size="sm" onClick={() => openEdit(row)}>
                                <i className="bx bx-upload me-1" /> Değiştir
                              </Button>
                              {hasImage && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => handleClear(row)}
                                >
                                  {m.video ? "Videoyu kaldır" : "Varsayılana dön"}
                                </Button>
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              ))
            )}
          </CardBody>
        </Card>

        <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
          <ModalHeader toggle={() => setModal(false)} tag="h4">
            {current ? `${meta(current).group} — ${meta(current).label}` : "Görsel"} — Değiştir
          </ModalHeader>
          <ModalBody>
            {current && (
              <>
                <p className="text-muted">{meta(current).hint}</p>

                {current.Site_Image_Url && !fileName && (
                  <div className="mb-3">
                    <Label className="form-label">{meta(current).video ? "Mevcut video" : "Mevcut görsel"}</Label>
                    <div>
                      <SlotPreview
                        value={current.Site_Image_Url}
                        video={meta(current).video}
                        style={{ maxWidth: "100%", width: meta(current).video ? 480 : undefined, height: meta(current).video ? 270 : undefined, maxHeight: 270, borderRadius: 6 }}
                      />
                    </div>
                  </div>
                )}

                {meta(current).video && (
                  <div className="mb-3">
                    <Label className="form-label">YouTube / Vimeo bağlantısı</Label>
                    <Input
                      value={videoUrl}
                      placeholder="https://www.youtube.com/watch?v=..."
                      onChange={(e) => { setVideoUrl(e.target.value); setFileName(null); }}
                    />
                    <div className="form-text">Bağlantı girilirse dosya yüklemenize gerek yoktur.</div>
                  </div>
                )}

                <div className="mb-3">
                  <Label className="form-label">{meta(current).video ? "…veya video dosyası yükle" : "Yeni görsel yükle"}</Label>
                  <Dropzone
                    getUploadParams={getUploadParams}
                    onChangeStatus={handleChangeStatus}
                    maxFiles={1}
                    multiple={false}
                    accept={meta(current).video ? "video/*" : "image/*"}
                    inputContent={meta(current).video ? "Videoyu buraya sürükleyin veya seçmek için tıklayın" : "Görseli buraya sürükleyin veya seçmek için tıklayın"}
                  />
                  {fileName && (
                    <div className="mt-2 text-success">
                      <i className="bx bx-check-circle me-1" />
                      Yüklendi: {fileName}
                    </div>
                  )}
                  <div className="form-text mt-2">
                    Sitenin hızlı açılması için görselin <strong>500 KB'ın altında</strong>
                    {" "}olması önerilir. Büyük fotoğrafları yüklemeden önce küçültün.
                  </div>
                </div>

                <div className="mb-3">
                  <Label className="form-label">Açıklama / alt metin (opsiyonel)</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>

                <div className="text-end">
                  <Button type="button" color="light" className="me-2" onClick={() => setModal(false)}>
                    İptal
                  </Button>
                  <Button color="success" onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner size="sm" className="me-1" /> : null}
                    Kaydet
                  </Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}
